import { useEffect, useState } from "react";
import { Search, Eye, Ban } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ReceiptDialog } from "@/components/ReceiptDialog";
import { VoidDialog } from "@/components/VoidDialog";
import { useAuth } from "@/context/AuthContext";
import { api, formatRupiah, formatDateTime, localDate, daysAgo, PAYMENT_LABEL } from "@/lib/api";

const METHOD_TONE = { tunai: "bg-emerald-50 text-emerald-700", qris: "bg-sky-50 text-sky-700", transfer: "bg-violet-50 text-violet-700" };

export default function Riwayat() {
  const [start, setStart] = useState(daysAgo(6));
  const [end, setEnd] = useState(localDate());
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState("selesai");
  const [voiding, setVoiding] = useState(null);
  const [reload, setReload] = useState(0);
  const isOwner = useAuth().user?.role === "owner";
  const voided = status === "dibatalkan";

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/transactions", { params: { start, end, q: q || undefined, status } }).then((r) => setRows(r.data));
    }, 250);
    return () => clearTimeout(t);
  }, [start, end, q, status, reload]);

  const total = rows.reduce((s, r) => s + r.total, 0);

  return (
    <div className="space-y-6" data-testid="riwayat-page">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 md:text-4xl">Riwayat Transaksi</h1>
        <p className="mt-1 text-sm text-slate-500">
          <span data-testid="riwayat-count">{rows.length}</span> transaksi {voided ? "dibatalkan" : ""} &middot; total <span className={`font-mono font-semibold ${voided ? "text-red-600 line-through" : "text-emerald-700"}`} data-testid="riwayat-total">{formatRupiah(total)}</span>
        </p>
      </div>

      <div className="flex w-fit rounded-full bg-white p-1 ring-1 ring-slate-200">
        {[["selesai", "Selesai"], ["dibatalkan", "Dibatalkan"]].map(([k, label]) => (
          <button key={k} onClick={() => setStatus(k)} data-testid={`riwayat-tab-${k}`}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${status === k ? (k === "dibatalkan" ? "bg-red-600 text-white" : "bg-slate-900 text-white") : "text-slate-600 hover:bg-slate-100"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Dari</Label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-10 bg-white" data-testid="riwayat-start-date" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Sampai</Label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-10 bg-white" data-testid="riwayat-end-date" />
        </div>
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} maxLength={40} onChange={(e) => setQ(e.target.value)} placeholder="Cari no. invoice..." className="h-10 bg-white pl-9" data-testid="riwayat-search-input" />
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>No. Invoice</TableHead>
              <TableHead>Waktu</TableHead>
              <TableHead className="text-center">Item</TableHead>
              <TableHead>Pembayaran</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Struk</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((t) => (
              <TableRow key={t.id} data-testid={`transaction-row-${t.id}`} onClick={() => setSelected(t)} className="cursor-pointer">
                <TableCell>
                  <p className="font-mono text-sm font-semibold text-slate-800">{t.invoice_no}</p>
                  {t.status === "dibatalkan" && (
                    <p className="mt-0.5 max-w-[260px] text-xs text-red-600" data-testid={`transaction-void-reason-${t.id}`}>
                      Dibatalkan oleh {t.voided_by}: {t.void_reason}
                    </p>
                  )}
                </TableCell>
                <TableCell className="text-sm text-slate-600">{formatDateTime(t.created_at)}</TableCell>
                <TableCell className="text-center font-mono">{t.items.reduce((s, i) => s + i.qty, 0)}</TableCell>
                <TableCell>
                  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${METHOD_TONE[t.payment_method]}`}>{PAYMENT_LABEL[t.payment_method]}</span>
                </TableCell>
                <TableCell className="text-right font-mono font-bold text-slate-900">{formatRupiah(t.total)}</TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => setSelected(t)} data-testid={`transaction-view-${t.id}`}>
                      <Eye className="mr-1.5 h-3.5 w-3.5" /> Lihat
                    </Button>
                    {isOwner && !voided && (
                      <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setVoiding(t)} data-testid={`transaction-void-${t.id}`}>
                        <Ban className="mr-1.5 h-3.5 w-3.5" /> Batalkan
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="py-10 text-center text-slate-500" data-testid="riwayat-empty">{voided ? "Tidak ada transaksi dibatalkan pada periode ini." : "Belum ada transaksi pada periode ini."}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <ReceiptDialog tx={selected} onClose={() => setSelected(null)} />
      <VoidDialog tx={voiding} onClose={() => setVoiding(null)} onDone={() => { setVoiding(null); setReload((n) => n + 1); }} />
    </div>
  );
}
